import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Save, Loader2, CheckCircle, XCircle, Coins, CreditCard, Repeat, ShieldCheck, ToggleLeft, ToggleRight, Type, Plus, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminPlanSettings() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Yeni Plan Ekleme Modalı State'leri
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const initialNewPlan = { name: '', description: '', token_amount: 0, price: 0.0, is_recurring: true, is_active: true };
    const [newPlan, setNewPlan] = useState(initialNewPlan);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/admin/plans`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPlans(response.data);
            setError(null);
        } catch (err) {
            console.error("Planlar çekilemedi:", err);
            setError("Veriler yüklenirken hata oluştu. Lütfen yetkinizi kontrol edin.");
        } finally {
            setLoading(false);
        }
    };

    // Mevcut Plan Güncelleme Handler'ları
    const handleInputChange = (planId, field, value) => {
        setPlans(plans.map(p => p.id === planId ? { ...p, [field]: value } : p));
    };

    const handleToggle = (planId, field) => {
        setPlans(plans.map(p => p.id === planId ? { ...p, [field]: !p[field] } : p));
    };

    const handleSave = async (plan) => {
        setSavingId(plan.id);
        setSuccessMessage('');
        setError(null);

        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_URL}/api/admin/plans/${plan.id}`,
                { 
                    name: plan.name,
                    description: plan.description || "",
                    token_amount: parseInt(plan.token_amount) || 0,
                    price: parseFloat(plan.price) || 0.0,
                    is_recurring: plan.is_recurring,
                    is_active: plan.is_active
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setSuccessMessage(`'${plan.name}' başarıyla güncellendi!`);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error("Güncelleme hatası:", err);
            setError(`${plan.name} güncellenemedi.`);
        } finally {
            setSavingId(null);
        }
    };

    // Yeni Plan Oluşturma Handler'ı
    const handleCreatePlan = async () => {
        if (!newPlan.name.trim()) {
            setError("Plan adı boş bırakılamaz.");
            return;
        }

        setIsCreating(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_URL}/api/admin/plans`,
                {
                    name: newPlan.name,
                    description: newPlan.description || "",
                    token_amount: parseInt(newPlan.token_amount) || 0,
                    price: parseFloat(newPlan.price) || 0.0,
                    is_recurring: newPlan.is_recurring,
                    is_active: newPlan.is_active
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setPlans([...plans, response.data]); // Yeni planı listeye ekle
            setSuccessMessage(`'${response.data.name}' başarıyla oluşturuldu!`);
            setIsAddModalOpen(false);
            setNewPlan(initialNewPlan); // Formu sıfırla
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error("Ekleme hatası:", err);
            setError("Yeni plan oluşturulurken bir hata meydana geldi.");
        } finally {
            setIsCreating(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <Loader2 className="animate-spin text-purple-500 w-12 h-12" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-gray-200 p-6 md:p-10 font-sans relative">
            <div className="max-w-7xl mx-auto">
                
                {/* Üst Başlık ve Yeni Ekle Butonu */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 border-b border-gray-800 pb-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-900/30 rounded-xl border border-purple-500/30">
                            <Package className="w-8 h-8 text-purple-400" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-extrabold text-white">Plan ve Abonelik Yönetimi</h1>
                            <p className="text-gray-400 mt-1">Sistemdeki paketlerin fiyatlarını, token miktarlarını düzenleyin veya yeni ekleyin.</p>
                        </div>
                    </div>
                    
                    <button 
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-bold shadow-lg shadow-purple-900/40 transition-all active:scale-95"
                    >
                        <Plus size={20} /> Yeni Plan Ekle
                    </button>
                </div>

                {/* Bildirimler */}
                {error && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-red-900/40 border border-red-700 rounded-xl flex items-center gap-3 text-red-400">
                        <XCircle className="w-5 h-5 flex-shrink-0" /> <p>{error}</p>
                    </motion.div>
                )}
                {successMessage && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-green-900/40 border border-green-700 rounded-xl flex items-center gap-3 text-green-400">
                        <CheckCircle className="w-5 h-5 flex-shrink-0" /> <p>{successMessage}</p>
                    </motion.div>
                )}

                {/* Planlar Grid Listesi */}
                <div className="grid grid-cols-1 gap-6">
                    {plans.map((plan) => (
                        <motion.div 
                            key={plan.id}
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className={`bg-gray-800 rounded-2xl border shadow-xl p-6 transition-colors duration-300
                            ${plan.is_active ? 'border-gray-700' : 'border-red-900/50 bg-gray-800/50 opacity-75'}`}
                        >
                            <div className="flex flex-col xl:flex-row gap-6 items-start xl:items-center">
                                
                                {/* 1. İsim ve Açıklama Alanı */}
                                <div className="w-full xl:w-1/3 space-y-3">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Type className="h-4 w-4 text-gray-500" /></div>
                                        <input
                                            type="text"
                                            value={plan.name}
                                            onChange={(e) => handleInputChange(plan.id, 'name', e.target.value)}
                                            className="block w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white font-bold focus:ring-2 focus:ring-purple-500"
                                            placeholder="Plan Adı (Örn: Pro Plan)"
                                        />
                                    </div>
                                    <div className="relative">
                                        <textarea
                                            value={plan.description || ''}
                                            onChange={(e) => handleInputChange(plan.id, 'description', e.target.value)}
                                            className="block w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-300 text-sm focus:ring-2 focus:ring-purple-500 resize-none h-16 custom-scrollbar"
                                            placeholder="Plan Açıklaması..."
                                        />
                                    </div>
                                </div>

                                {/* 2. Token ve Fiyat Alanı */}
                                <div className="w-full xl:w-1/4 space-y-3">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Coins className="h-4 w-4 text-yellow-500" /></div>
                                        <input
                                            type="number"
                                            value={plan.token_amount}
                                            onChange={(e) => handleInputChange(plan.id, 'token_amount', e.target.value)}
                                            className="block w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white font-medium focus:ring-2 focus:ring-purple-500"
                                            placeholder="Token Miktarı"
                                        />
                                    </div>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><CreditCard className="h-4 w-4 text-green-400" /></div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={plan.price}
                                            onChange={(e) => handleInputChange(plan.id, 'price', e.target.value)}
                                            className="block w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white font-medium focus:ring-2 focus:ring-purple-500"
                                            placeholder="Fiyat (₺)"
                                        />
                                    </div>
                                </div>

                                {/* 3. Toggles (Abonelik ve Aktiflik Durumu) */}
                                <div className="w-full xl:w-1/4 flex flex-col gap-3 justify-center">
                                    <button 
                                        onClick={() => handleToggle(plan.id, 'is_recurring')}
                                        className={`flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all ${plan.is_recurring ? 'bg-indigo-900/30 border-indigo-500/50 text-indigo-300' : 'bg-gray-900 border-gray-600 text-gray-400'}`}
                                    >
                                        <div className="flex items-center gap-2 text-sm font-semibold">
                                            <Repeat size={16} /> <span>Abonelik (Tekrarlı)</span>
                                        </div>
                                        {plan.is_recurring ? <ToggleRight size={24} className="text-indigo-400" /> : <ToggleLeft size={24} />}
                                    </button>

                                    <button 
                                        onClick={() => handleToggle(plan.id, 'is_active')}
                                        className={`flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all ${plan.is_active ? 'bg-emerald-900/30 border-emerald-500/50 text-emerald-300' : 'bg-red-900/30 border-red-500/50 text-red-300'}`}
                                    >
                                        <div className="flex items-center gap-2 text-sm font-semibold">
                                            <ShieldCheck size={16} /> <span>{plan.is_active ? 'Satışta (Aktif)' : 'Devre Dışı (Pasif)'}</span>
                                        </div>
                                        {plan.is_active ? <ToggleRight size={24} className="text-emerald-400" /> : <ToggleLeft size={24} className="text-red-400" />}
                                    </button>
                                </div>

                                {/* 4. Kaydet Butonu */}
                                <div className="w-full xl:w-[auto] flex-1 flex justify-end xl:justify-center mt-2 xl:mt-0">
                                    <button
                                        onClick={() => handleSave(plan)}
                                        disabled={savingId === plan.id}
                                        className={`w-full xl:w-32 h-14 flex items-center justify-center gap-2 rounded-xl font-bold transition-all shadow-lg
                                        ${savingId === plan.id 
                                            ? 'bg-purple-600/50 cursor-not-allowed text-white' 
                                            : 'bg-purple-600 hover:bg-purple-500 text-white hover:shadow-purple-900/50 active:scale-95'}`}
                                    >
                                        {savingId === plan.id ? (
                                            <Loader2 className="w-6 h-6 animate-spin" />
                                        ) : (
                                            <>
                                                <Save className="w-5 h-5" />
                                                <span>Kaydet</span>
                                            </>
                                        )}
                                    </button>
                                </div>

                            </div>
                        </motion.div>
                    ))}

                    {plans.length === 0 && !loading && (
                        <div className="p-12 bg-gray-800 rounded-2xl border border-gray-700 text-center text-gray-500">
                            <Package className="w-16 h-16 mx-auto mb-4 opacity-20" />
                            <p className="text-lg font-medium mb-4">Sistemde henüz oluşturulmuş bir plan bulunmuyor.</p>
                            <button onClick={() => setIsAddModalOpen(true)} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors">İlk Planı Ekle</button>
                        </div>
                    )}
                </div>

            </div>

            {/* 🚨 YENİ PLAN EKLEME MODALI 🚨 */}
            <AnimatePresence>
                {isAddModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/60">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
                        >
                            <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-gray-900/50">
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <Plus className="text-purple-400" /> Yeni Plan Oluştur
                                </h3>
                                <button onClick={() => setIsAddModalOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                                    <X size={24} />
                                </button>
                            </div>
                            
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Plan Adı</label>
                                    <input 
                                        type="text" 
                                        value={newPlan.name} 
                                        onChange={(e) => setNewPlan({...newPlan, name: e.target.value})} 
                                        className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-purple-500 outline-none" 
                                        placeholder="Örn: Premium Paket" 
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Açıklama</label>
                                    <textarea 
                                        value={newPlan.description} 
                                        onChange={(e) => setNewPlan({...newPlan, description: e.target.value})} 
                                        className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-purple-500 outline-none resize-none h-20 custom-scrollbar" 
                                        placeholder="Paketin avantajları..." 
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Fiyat (₺)</label>
                                        <input 
                                            type="number" step="0.01" 
                                            value={newPlan.price} 
                                            onChange={(e) => setNewPlan({...newPlan, price: e.target.value})} 
                                            className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-purple-500 outline-none" 
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Token Miktarı</label>
                                        <input 
                                            type="number" 
                                            value={newPlan.token_amount} 
                                            onChange={(e) => setNewPlan({...newPlan, token_amount: e.target.value})} 
                                            className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-purple-500 outline-none" 
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-4 pt-2">
                                    <button 
                                        onClick={() => setNewPlan({...newPlan, is_recurring: !newPlan.is_recurring})}
                                        className={`flex-1 flex items-center justify-between p-3 rounded-xl border transition-all ${newPlan.is_recurring ? 'bg-indigo-900/30 border-indigo-500 text-indigo-300' : 'bg-gray-900 border-gray-700 text-gray-400'}`}
                                    >
                                        <span className="text-sm font-semibold">Abonelik</span>
                                        {newPlan.is_recurring ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                    </button>
                                    <button 
                                        onClick={() => setNewPlan({...newPlan, is_active: !newPlan.is_active})}
                                        className={`flex-1 flex items-center justify-between p-3 rounded-xl border transition-all ${newPlan.is_active ? 'bg-emerald-900/30 border-emerald-500 text-emerald-300' : 'bg-gray-900 border-gray-700 text-gray-400'}`}
                                    >
                                        <span className="text-sm font-semibold">Aktif</span>
                                        {newPlan.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                    </button>
                                </div>
                            </div>
                            
                            <div className="px-6 py-4 border-t border-gray-700 bg-gray-900/50 flex justify-end gap-3">
                                <button onClick={() => setIsAddModalOpen(false)} className="px-5 py-2.5 text-gray-400 hover:text-white font-semibold transition-colors">Vazgeç</button>
                                <button 
                                    onClick={handleCreatePlan} 
                                    disabled={isCreating || !newPlan.name.trim()}
                                    className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/50 text-white rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg active:scale-95"
                                >
                                    {isCreating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Oluştur
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}